import { useState, useCallback } from "react";
import { axiosInstance } from "@/App";
import Layout from "@/components/Layout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { 
  Search, 
  User, 
  Mail, 
  Phone, 
  MapPin, 
  Building2, 
  Zap, 
  Users, 
  Calendar,
  FileText,
  Save,
  Edit,
  X,
  Hash,
  ChevronDown,
  ChevronUp,
  Home
} from "lucide-react";

// ---- Extracted outside to prevent re-mount on parent re-render ----

function InfoCard({ title, icon: Icon, children, className = "", id, isExpanded, onToggle }) {
  return (
    <Card className={`border-0 shadow-lg ${className}`}>
      <CardHeader 
        className="pb-3 cursor-pointer hover:bg-gray-50 transition-colors" 
        onClick={() => onToggle(id)}
      >
        <CardTitle className="text-lg flex items-center justify-between">
          <span className="flex items-center gap-2">
            <Icon className="w-5 h-5 text-blue-600" />
            {title}
          </span>
          {isExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
        </CardTitle>
      </CardHeader>
      {isExpanded && (
        <CardContent className="space-y-4">
          {children}
        </CardContent>
      )}
    </Card>
  );
}

function formatDate(date) {
  if (!date) return "-";
  try {
    return new Date(date).toLocaleDateString('pt-BR');
  } catch {
    return "-";
  }
}

function formatDateForInput(date) {
  if (!date) return "";
  try {
    return new Date(date).toISOString().split('T')[0];
  } catch {
    return "";
  }
}

function Field({ label, value, field, type = "text", options = null, editMode, editData, onInputChange }) {
  if (editMode) {
    if (options) {
      return (
        <div className="space-y-1">
          <Label className="text-xs text-gray-500">{label}</Label>
          <Select value={editData[field] || ""} onValueChange={(v) => onInputChange(field, v)}>
            <SelectTrigger className="h-9">
              <SelectValue placeholder="Selecione..." />
            </SelectTrigger>
            <SelectContent>
              {options.map(opt => (
                <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      );
    }
    
    if (field === 'contracted_kwh') {
      return (
        <div className="space-y-1">
          <Label className="text-xs text-gray-500">{label}</Label>
          <Input
            type="number"
            step="1"
            min="200"
            value={editData[field] || ""}
            onChange={(e) => onInputChange(field, parseInt(e.target.value, 10) || '')}
            className="h-9"
            placeholder="Mínimo 200"
          />
          <p className="text-xs text-gray-400">Mínimo: 200 kWh (apenas inteiros)</p>
        </div>
      );
    }
    
    return (
      <div className="space-y-1">
        <Label className="text-xs text-gray-500">{label}</Label>
        <Input
          type={type}
          value={type === 'date' ? formatDateForInput(editData[field]) : (editData[field] || "")}
          onChange={(e) => onInputChange(field, e.target.value)}
          className="h-9"
        />
      </div>
    );
  }
  
  return (
    <div className="space-y-1">
      <Label className="text-xs text-gray-500">{label}</Label>
      <p className="text-sm font-medium text-gray-800">
        {type === 'date' ? formatDate(value) : (value || "-")}
      </p>
    </div>
  );
}

// ---- Main Component ----

export default function ConsultaCooperado({ user, onLogout }) {
  const [searchTerm, setSearchTerm] = useState("");
  const [searchType, setSearchType] = useState("cpf_cnpj"); // cpf_cnpj, installation_number, name
  const [searching, setSearching] = useState(false);
  const [searchResult, setSearchResult] = useState(null);
  const [selectedInstallation, setSelectedInstallation] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [editData, setEditData] = useState({});
  const [saving, setSaving] = useState(false);
  const [partners, setPartners] = useState([]);
  const [expandedCards, setExpandedCards] = useState({});

  const handleSearch = async () => {
    if (!searchTerm.trim()) {
      toast.error("Digite um termo para buscar");
      return;
    }

    setSearching(true);
    setSearchResult(null);
    setSelectedInstallation(null);
    setEditMode(false);

    try {
      let response;
      
      if (searchType === "cpf_cnpj") {
        const cleanSearch = searchTerm.replace(/[^\d]/g, '');
        response = await axiosInstance.get(`/cooperatives/search-by-document`, {
          params: { document: cleanSearch }
        });
      } else if (searchType === "installation_number") {
        response = await axiosInstance.get(`/cooperatives/search-by-installation`, {
          params: { installation_number: searchTerm.trim() }
        });
      } else if (searchType === "name") {
        response = await axiosInstance.get(`/cooperatives/search-by-name`, {
          params: { name: searchTerm.trim() }
        });
      }
      
      if (response.data) {
        setSearchResult(response.data);
        
        // Se houver apenas uma instalação, seleciona automaticamente
        if (response.data.installations?.length === 1) {
          setSelectedInstallation(response.data.installations[0]);
          setEditData(response.data.installations[0]);
        }
        
        toast.success(`${response.data.total} instalação(ões) encontrada(s)!`);
        
        // Carregar parceiros para o select
        const partnersRes = await axiosInstance.get("/partners");
        setPartners(partnersRes.data);
      }
    } catch (error) {
      if (error.response?.status === 404) {
        toast.error("Cooperado não encontrado");
      } else {
        toast.error(error.response?.data?.detail || "Erro ao buscar cooperado");
      }
    } finally {
      setSearching(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  const handleSelectInstallation = (installation) => {
    setSelectedInstallation(installation);
    setEditData(installation);
    setEditMode(false);
  };

  const handleEdit = () => {
    setEditMode(true);
    setEditData({ ...selectedInstallation });
  };

  const handleCancel = () => {
    setEditMode(false);
    setEditData({ ...selectedInstallation });
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axiosInstance.put(`/cooperatives/${selectedInstallation.id}/json`, editData);
      
      // Atualizar a instalação na lista
      const updatedInstallations = searchResult.installations.map(inst => 
        inst.id === selectedInstallation.id ? editData : inst
      );
      setSearchResult({ ...searchResult, installations: updatedInstallations });
      setSelectedInstallation(editData);
      setEditMode(false);
      toast.success("Dados atualizados com sucesso!");
    } catch (error) {
      console.error("Error saving:", error);
      const errorDetail = error.response?.data?.detail;
      if (Array.isArray(errorDetail)) {
        const messages = errorDetail.map(e => e.msg || e.message || JSON.stringify(e)).join(', ');
        toast.error(`Erro de validação: ${messages}`);
      } else if (typeof errorDetail === 'object') {
        toast.error(`Erro: ${errorDetail.msg || JSON.stringify(errorDetail)}`);
      } else {
        toast.error(errorDetail || "Erro ao salvar alterações");
      }
    } finally {
      setSaving(false);
    }
  };

  const handleInputChange = useCallback((field, value) => {
    setEditData(prev => ({ ...prev, [field]: value }));
  }, []);

  const toggleCard = useCallback((cardId) => {
    setExpandedCards(prev => ({ ...prev, [cardId]: !prev[cardId] }));
  }, []);

  return (
    <Layout user={user} onLogout={onLogout}>
      <div className="space-y-6">
        {/* Header */}
        <div>
          <h1 className="text-2xl md:text-4xl font-bold text-gray-800 mb-2">Consulta Cooperado</h1>
          <p className="text-gray-600">Busque e edite os dados do cooperado por CPF ou CNPJ</p>
        </div>

        {/* Search Card */}
        <Card className="border-0 shadow-lg">
          <CardContent className="pt-6">
              <div className="flex items-center gap-3 mb-4">
                <Label className="text-sm font-medium whitespace-nowrap">Buscar por:</Label>
                <div className="flex gap-2">
                  <Button
                    variant={searchType === "cpf_cnpj" ? "default" : "outline"}
                    size="sm"
                    onClick={() => { setSearchType("cpf_cnpj"); setSearchTerm(""); }}
                    className={searchType === "cpf_cnpj" ? "bg-blue-600" : ""}
                  >
                    CPF/CNPJ
                  </Button>
                  <Button
                    variant={searchType === "installation_number" ? "default" : "outline"}
                    size="sm"
                    onClick={() => { setSearchType("installation_number"); setSearchTerm(""); }}
                    className={searchType === "installation_number" ? "bg-blue-600" : ""}
                  >
                    Nº Instalação
                  </Button>
                  <Button
                    variant={searchType === "name" ? "default" : "outline"}
                    size="sm"
                    onClick={() => { setSearchType("name"); setSearchTerm(""); }}
                    className={searchType === "name" ? "bg-blue-600" : ""}
                  >
                    Nome
                  </Button>
                </div>
              </div>
              <div className="flex gap-4 items-end">
                <div className="flex-1">
                  <Label htmlFor="search" className="mb-2 block">
                    {searchType === "cpf_cnpj" && "CPF ou CNPJ"}
                    {searchType === "installation_number" && "Número de Instalação"}
                    {searchType === "name" && "Nome do Cooperado"}
                  </Label>
                  <Input
                    id="search"
                    placeholder={
                      searchType === "cpf_cnpj" 
                        ? "Digite o CPF ou CNPJ (apenas números ou formatado)" 
                        : searchType === "installation_number"
                        ? "Digite o número de instalação"
                        : "Digite o nome do cooperado"
                    }
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    onKeyPress={handleKeyPress}
                    className="text-lg"
                    data-testid="search-input"
                  />
                </div>
                <Button 
                  onClick={handleSearch} 
                  disabled={searching}
                  className="bg-gradient-to-r from-blue-500 to-cyan-600 hover:from-blue-600 hover:to-cyan-700 h-10 px-6"
                  data-testid="search-button"
                >
                  <Search className="w-4 h-4 mr-2" />
                  {searching ? "Buscando..." : "Buscar"}
                </Button>
              </div>
          </CardContent>
        </Card>

        {/* Lista de Instalações */}
        {searchResult && searchResult.installations?.length > 0 && (
          <Card className="border-0 shadow-lg">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Home className="w-5 h-5 text-blue-600" />
                Instalações Encontradas ({searchResult.total})
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {searchResult.installations.map((installation, index) => (
                  <div
                    key={installation.id || index}
                    onClick={() => handleSelectInstallation(installation)}
                    className={`p-4 rounded-lg border-2 cursor-pointer transition-all ${
                      selectedInstallation?.id === installation.id
                        ? 'border-blue-500 bg-blue-50'
                        : 'border-gray-200 hover:border-blue-300 hover:bg-gray-50'
                    }`}
                    data-testid={`installation-${index}`}
                  >
                    <div className="flex justify-between items-start">
                      <div>
                        <p className="font-semibold text-gray-800">
                          <Zap className="w-4 h-4 inline mr-2 text-yellow-500" />
                          Instalação: {installation.installation_number || 'N/A'}
                        </p>
                        <p className="text-sm text-gray-600 mt-1">
                          <Building2 className="w-3 h-3 inline mr-1" />
                          {installation.distributor || 'Distribuidora não informada'}
                        </p>
                        <p className="text-sm text-gray-600">
                          <MapPin className="w-3 h-3 inline mr-1" />
                          {installation.city || 'Cidade não informada'}{installation.state ? `, ${installation.state}` : ''}
                        </p>
                      </div>
                      <div className="text-right">
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                          installation.situation === 'active' 
                            ? 'bg-green-100 text-green-700' 
                            : installation.situation === 'inactive'
                            ? 'bg-red-100 text-red-700'
                            : 'bg-yellow-100 text-yellow-700'
                        }`}>
                          {installation.situation === 'active' ? 'Ativo' : installation.situation === 'inactive' ? 'Inativo' : 'Suspenso'}
                        </span>
                        <p className="text-sm text-gray-500 mt-2">
                          {installation.contracted_kwh ? `${installation.contracted_kwh} kWh` : '-'}
                        </p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Detalhes da Instalação Selecionada */}
        {selectedInstallation && (
          <>
            {/* Action Bar */}
            <div className="flex justify-between items-center">
              <h2 className="text-xl font-semibold text-gray-700">
                Detalhes da Instalação: {selectedInstallation.installation_number}
              </h2>
              <div className="flex gap-2">
                {editMode ? (
                  <>
                    <Button variant="outline" onClick={handleCancel} disabled={saving}>
                      <X className="w-4 h-4 mr-2" />
                      Cancelar
                    </Button>
                    <Button 
                      onClick={handleSave} 
                      disabled={saving}
                      className="bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700"
                      data-testid="save-button"
                    >
                      <Save className="w-4 h-4 mr-2" />
                      {saving ? "Salvando..." : "Salvar Alterações"}
                    </Button>
                  </>
                ) : (
                  <Button 
                    onClick={handleEdit} 
                    className="bg-gradient-to-r from-blue-500 to-cyan-600"
                    data-testid="edit-button"
                  >
                    <Edit className="w-4 h-4 mr-2" />
                    Editar Dados
                  </Button>
                )}
              </div>
            </div>

            {/* Cards Grid */}
            <div className="grid md:grid-cols-2 gap-6">
              {/* Dados Pessoais */}
              <InfoCard title="Dados Pessoais" icon={User} id="personal" isExpanded={expandedCards["personal"] !== false} onToggle={toggleCard}>
                <Field 
                  label="Tipo de Cadastro" 
                  value={selectedInstallation.registration_type === 'PJ' ? 'Pessoa Jurídica' : 'Pessoa Física'} 
                  field="registration_type"
                  editMode={editMode} editData={editData} onInputChange={handleInputChange}
                  options={[
                    { value: 'PF', label: 'Pessoa Física' },
                    { value: 'PJ', label: 'Pessoa Jurídica' }
                  ]}
                />
                <Field label="Nome / Razão Social" value={selectedInstallation.name} field="name" editMode={editMode} editData={editData} onInputChange={handleInputChange} />
                <Field label="CPF / CNPJ" value={selectedInstallation.cpf_cnpj || selectedInstallation.cpf} field="cpf_cnpj" editMode={editMode} editData={editData} onInputChange={handleInputChange} />
                <Field label="Data de Nascimento" value={selectedInstallation.birth_date} field="birth_date" type="date" editMode={editMode} editData={editData} onInputChange={handleInputChange} />
                
                {(selectedInstallation.registration_type === 'PJ' || editData.registration_type === 'PJ') && (
                  <>
                    <Field label="CPF Representante Legal" value={selectedInstallation.legal_representative_cpf} field="legal_representative_cpf" editMode={editMode} editData={editData} onInputChange={handleInputChange} />
                    <Field label="Nome Representante Legal" value={selectedInstallation.legal_representative_name} field="legal_representative_name" editMode={editMode} editData={editData} onInputChange={handleInputChange} />
                  </>
                )}
              </InfoCard>

              {/* Contato */}
              <InfoCard title="Contato" icon={Mail} id="contact" isExpanded={expandedCards["contact"] !== false} onToggle={toggleCard}>
                <Field label="Email" value={selectedInstallation.email} field="email" type="email" editMode={editMode} editData={editData} onInputChange={handleInputChange} />
                <Field label="Telefone" value={selectedInstallation.phone} field="phone" editMode={editMode} editData={editData} onInputChange={handleInputChange} />
              </InfoCard>

              {/* Endereço */}
              <InfoCard title="Endereço" icon={MapPin} id="address" isExpanded={expandedCards["address"] !== false} onToggle={toggleCard}>
                <Field label="Rua" value={selectedInstallation.street} field="street" editMode={editMode} editData={editData} onInputChange={handleInputChange} />
                <div className="grid grid-cols-2 gap-4">
                  <Field label="Número" value={selectedInstallation.number} field="number" editMode={editMode} editData={editData} onInputChange={handleInputChange} />
                  <Field label="Complemento" value={selectedInstallation.complement} field="complement" editMode={editMode} editData={editData} onInputChange={handleInputChange} />
                </div>
                <Field label="Bairro" value={selectedInstallation.neighborhood} field="neighborhood" editMode={editMode} editData={editData} onInputChange={handleInputChange} />
                <div className="grid grid-cols-2 gap-4">
                  <Field label="Cidade" value={selectedInstallation.city} field="city" editMode={editMode} editData={editData} onInputChange={handleInputChange} />
                  <Field label="Estado" value={selectedInstallation.state} field="state" editMode={editMode} editData={editData} onInputChange={handleInputChange} />
                </div>
                <Field label="CEP" value={selectedInstallation.zip_code} field="zip_code" editMode={editMode} editData={editData} onInputChange={handleInputChange} />
              </InfoCard>

              {/* Instalação */}
              <InfoCard title="Dados da Instalação" icon={Zap} id="installation" isExpanded={expandedCards["installation"] !== false} onToggle={toggleCard}>
                <Field label="Nº da Instalação" value={selectedInstallation.installation_number} field="installation_number" editMode={editMode} editData={editData} onInputChange={handleInputChange} />
                <Field label="Nº da Instalação (UC Antiga)" value={selectedInstallation.previous_installation_number} field="previous_installation_number" editMode={editMode} editData={editData} onInputChange={handleInputChange} />
                <Field 
                  label="Classe da Instalação" 
                  value={selectedInstallation.installation_class} 
                  field="installation_class"
                  editMode={editMode} editData={editData} onInputChange={handleInputChange}
                  options={[
                    { value: 'Monofásico', label: 'Monofásico' },
                    { value: 'Bifásico', label: 'Bifásico' },
                    { value: 'Trifásico', label: 'Trifásico' }
                  ]}
                />
                <Field label="Modalidade de Compensação" value={selectedInstallation.compensation_modality} field="compensation_modality" editMode={editMode} editData={editData} onInputChange={handleInputChange} options={[{value: "Geração Compartilhada", label: "Geração Compartilhada"}, {value: "Autoconsumo Remoto", label: "Autoconsumo Remoto"}, {value: "Autoconsumo C", label: "Autoconsumo C"}, {value: "Autoconsumo Remoto - Usina", label: "Autoconsumo Remoto - Usina"}]} />
                <Field label="Nº Instalação Geradora" value={selectedInstallation.generator_installation_number} field="generator_installation_number" editMode={editMode} editData={editData} onInputChange={handleInputChange} />
                <Field label="Nº do Agente" value={selectedInstallation.agent_number} field="agent_number" editMode={editMode} editData={editData} onInputChange={handleInputChange} />
                <Field label="kWh Contratados" value={selectedInstallation.contracted_kwh} field="contracted_kwh" type="number" editMode={editMode} editData={editData} onInputChange={handleInputChange} />
                <Field label="Observações" value={selectedInstallation.installation_notes} field="installation_notes" editMode={editMode} editData={editData} onInputChange={handleInputChange} />
              </InfoCard>

              {/* Distribuidora */}
              <InfoCard title="Distribuidora" icon={Building2} id="distributor" isExpanded={expandedCards["distributor"] !== false} onToggle={toggleCard}>
                <Field 
                  label="Distribuidora" 
                  value={selectedInstallation.distributor} 
                  field="distributor"
                  editMode={editMode} editData={editData} onInputChange={handleInputChange}
                  options={[
                    { value: 'CELESC', label: 'CELESC' },
                    { value: 'COPEL', label: 'COPEL' },
                    { value: 'RGE', label: 'RGE' },
                    { value: 'CEMIG', label: 'CEMIG' },
                    { value: 'CERBRANORTE', label: 'CERBRANORTE' },
                    { value: 'CEEE-Equatorial', label: 'CEEE-Equatorial' },
                    { value: 'EQUATORIAL-GO', label: 'EQUATORIAL-GO' },
                    { value: 'ENERGISA-MG', label: 'ENERGISA-MG' },
                    { value: 'COELBA-BA', label: 'COELBA-BA' }
                  ]}
                />
                <Field label="Login Distribuidora" value={selectedInstallation.distributor_login} field="distributor_login" editMode={editMode} editData={editData} onInputChange={handleInputChange} />
                <Field label="Senha Distribuidora" value={selectedInstallation.distributor_password} field="distributor_password" editMode={editMode} editData={editData} onInputChange={handleInputChange} />
              </InfoCard>

              {/* Parceiro e Comissão */}
              <InfoCard title="Parceiro e Comissão" icon={Users} id="partner" isExpanded={expandedCards["partner"] !== false} onToggle={toggleCard}>
                {editMode ? (
                  <div className="space-y-1">
                    <Label className="text-xs text-gray-500">Parceiro</Label>
                    <Select value={editData.partner_id || ""} onValueChange={(v) => {
                      const partner = partners.find(p => p.id === v);
                      handleInputChange('partner_id', v);
                      handleInputChange('partner_name', partner?.name || '');
                    }}>
                      <SelectTrigger className="h-9">
                        <SelectValue placeholder="Selecione o parceiro" />
                      </SelectTrigger>
                      <SelectContent>
                        {partners.map(p => (
                          <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                ) : (
                  <Field label="Parceiro" value={selectedInstallation.partner_name} field="partner_name" editMode={editMode} editData={editData} onInputChange={handleInputChange} />
                )}
                <Field label="Comissão do Parceiro (%)" value={selectedInstallation.partner_commission} field="partner_commission" type="number" editMode={editMode} editData={editData} onInputChange={handleInputChange} />
              </InfoCard>

              {/* Contrato */}
              <InfoCard title="Dados do Contrato" icon={FileText} id="contract" isExpanded={expandedCards["contract"] !== false} onToggle={toggleCard}>
                <Field label="Data de Assinatura" value={selectedInstallation.signature_date} field="signature_date" type="date" editMode={editMode} editData={editData} onInputChange={handleInputChange} />
                <Field label="Desconto na Tarifa (%)" value={selectedInstallation.tariff_discount} field="tariff_discount" type="number" editMode={editMode} editData={editData} onInputChange={handleInputChange} />
                <Field 
                  label="Situação" 
                  value={selectedInstallation.situation === 'active' ? 'Ativo' : selectedInstallation.situation === 'inactive' ? 'Inativo' : 'Suspenso'} 
                  field="situation"
                  editMode={editMode} editData={editData} onInputChange={handleInputChange}
                  options={[
                    { value: 'active', label: 'Ativo' },
                    { value: 'inactive', label: 'Inativo' },
                    { value: 'suspended', label: 'Suspenso' }
                  ]}
                />
              </InfoCard>

              {/* Informações do Sistema */}
              <InfoCard title="Informações do Sistema" icon={Hash} className="md:col-span-2" id="system" isExpanded={expandedCards["system"] !== false} onToggle={toggleCard}>
                <div className="grid md:grid-cols-3 gap-4">
                  <div className="space-y-1">
                    <Label className="text-xs text-gray-500">ID do Registro</Label>
                    <p className="text-sm font-mono text-gray-600">{selectedInstallation.id}</p>
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs text-gray-500">Data de Cadastro</Label>
                    <p className="text-sm text-gray-800">{formatDate(selectedInstallation.created_at)}</p>
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs text-gray-500">Origem</Label>
                    <p className="text-sm text-gray-800">{selectedInstallation.origin || "-"}</p>
                  </div>
                </div>
              </InfoCard>
            </div>
          </>
        )}

        {/* Empty State */}
        {!searchResult && !searching && (
          <Card className="border-0 shadow-lg">
            <CardContent className="py-16 text-center">
              <Search className="w-16 h-16 mx-auto text-gray-300 mb-4" />
              <h3 className="text-xl font-semibold text-gray-600 mb-2">Busque um Cooperado</h3>
              <p className="text-gray-500">Digite o CPF ou CNPJ no campo acima para consultar os dados</p>
            </CardContent>
          </Card>
        )}
      </div>
    </Layout>
  );
}
